import React from 'react';
import { Reveal, SectionEyebrow } from '../components/ui-bits';
import Seo from '../components/Seo';

export default function Terms() {
  return (
    <div data-testid="terms-page" className="bg-white">
      <Seo
        title="Terms of Use"
        description="Website terms of use for David Ball Management. Rules for browsing dbmtalent.com, intellectual property, third-party links and limits on liability."
        path="/terms"
      />
      <section className="pt-40 pb-16 lg:pt-48 lg:pb-24 bg-[#faf8f4]">
        <div className="max-w-[1500px] mx-auto px-6 lg:px-10">
          <Reveal>
            <SectionEyebrow>Legal</SectionEyebrow>
          </Reveal>
          <Reveal delay={120}>
            <h1 className="font-heading uppercase text-[#002b5c] text-5xl sm:text-6xl md:text-7xl mt-8 leading-[0.9]">
              Terms <span className="italic font-light">of Use</span>
            </h1>
          </Reveal>
        </div>
      </section>

      <article className="max-w-[820px] mx-auto px-6 lg:px-8 py-20 lg:py-28">
        <p className="text-[11px] tracking-[0.22em] uppercase text-[#6b6b6b] mb-12">
          Last updated · 28 February 2026
        </p>

        <Section title="About these terms">
          <p>
            These Terms of Use govern your access to and use of the website{' '}
            <a href="https://dbmtalent.com" className="gold-underline">www.dbmtalent.com</a> (the
            "Site"), operated by David Ball Limited, a company registered in England &amp; Wales
            with correspondence address at 29 Holmdale Road, London BR7 6BY.
          </p>
          <p>
            By using the Site you agree to these terms. If you do not accept them,
            please do not use the Site.
          </p>
        </Section>

        <Section title="Who we are and what we do">
          <p>
            David Ball Management is a London-based agency representing actors and writers across
            television, film, theatre, commercials and voice. Information on the Site
            about our clients, services and industry is provided in good faith for
            general reference only.
          </p>
        </Section>

        <Section title="Acceptable use">
          <p>You agree to use the Site only for lawful purposes. You must not:</p>
          <ul>
            <li>use the Site in any way that breaches applicable UK law or regulation;</li>
            <li>attempt to gain unauthorised access to the Site, its server or any connected database;</li>
            <li>introduce viruses, trojans, worms or other malicious material;</li>
            <li>scrape, harvest or systematically extract content or client data from the Site without our prior written consent;</li>
            <li>use the Site to send unsolicited communications to us or to anyone whose details are shown on the Site.</li>
          </ul>
        </Section>

        <Section title="Intellectual property">
          <p>
            All content on the Site — including text, photography, headshots, showreels,
            logos and design — is either owned by David Ball Limited, licensed to us or used
            with the permission of the individual client, photographer or rights-holder.
          </p>
          <p>
            You may view and print pages for your own personal, non-commercial use. Any
            other reproduction, redistribution, adaptation or commercial use requires our
            prior written permission. Casting professionals viewing client material should
            do so only in the context of considering our clients for professional
            opportunities.
          </p>
        </Section>

        <Section title="Client information">
          <p>
            Client biographies, credits, headshots and showreels are published for
            professional casting and industry reference. Any use of a client's image,
            biography or voice for advertising, promotion or commercial purposes without
            written consent from David Ball Management is prohibited.
          </p>
        </Section>

        <Section title="Enquiries and submissions">
          <p>
            Any material you send to us — including representation enquiries, general
            correspondence and scripts — is welcome, but by sending it you acknowledge
            that we cannot guarantee confidentiality of unsolicited material and that we
            may already be developing or considering similar work. We do not commit to
            reviewing, returning or retaining unsolicited scripts.
          </p>
        </Section>

        <Section title="Third-party links">
          <p>
            The Site links to external services including Spotlight, YouTube, Google
            Maps and social networks. We are not responsible for the content, terms or
            privacy practices of any third-party site.
          </p>
        </Section>

        <Section title="Availability of the Site">
          <p>
            We aim to keep the Site available at all times but do not guarantee
            uninterrupted access. We may suspend, withdraw or restrict availability of
            the Site or any part of it without notice.
          </p>
        </Section>

        <Section title="Our liability">
          <p>
            To the fullest extent permitted by law, David Ball Limited excludes all
            conditions, warranties and representations that may apply to the Site or its
            content. We will not be liable for any indirect or consequential loss arising
            from your use of the Site.
          </p>
          <p>
            Nothing in these terms limits our liability for death or personal injury
            caused by our negligence, for fraud, or for any liability that cannot be
            excluded under UK law.
          </p>
        </Section>

        <Section title="Changes to these terms">
          <p>
            We may update these terms from time to time. Continued use of the Site after
            any change constitutes acceptance of the updated terms.
          </p>
        </Section>

        <Section title="Governing law">
          <p>
            These terms are governed by the laws of England and Wales. The courts of
            England and Wales have exclusive jurisdiction to settle any dispute arising
            out of or in connection with the Site.
          </p>
        </Section>

        <Section title="Contact">
          <p>
            For any question about these terms, please write to{' '}
            <a href="mailto:office@dbmtalent.com" className="gold-underline">office@dbmtalent.com</a>{' '}
            or call <a href="tel:+442033550561" className="gold-underline">+44 (0) 203 355 0561</a>.
          </p>
        </Section>
      </article>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="mb-14">
      <h2 className="font-heading uppercase text-2xl md:text-3xl text-[#002b5c] mb-6 leading-tight">{title}</h2>
      <div className="space-y-4 text-[#333] leading-[1.85] text-base">
        {children}
      </div>
    </section>
  );
}
